import * as React from "react";

import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";

import { ContextType } from "./contextType";
import {
  ContextDisplayNames,
  useClientViewingContext,
} from "./hooks/useClientViewingContext";

/**
 * AIAgentThreadContext provides UI state only - no data fetching or mutations.
 * Data fetching and mutations are handled by the AgentThread component.
 */
interface AIAgentThreadContextValue {
  // UI state
  autoEnabled: boolean;
  currentThreadId: string | undefined;
  context: ClientViewingContext;
  contextDisplayNames: ContextDisplayNames;
  /** The model the user explicitly picked for the next new thread, or
   *  undefined when they have not picked one (the server then applies the
   *  org default). Cleared whenever the thread changes. */
  modelProfile: string | undefined;

  // UI state setters
  setAutoEnabled: (value: boolean) => void;
  setModelProfile: (profile: string) => void;

  // Convenience handlers
  handleContextDelete: (
    contextType: ContextType,
    contextContent: string,
  ) => void;
  /**
   * Resolve the ClientViewingContext for a send: the reactive `context`
   * (deletions applied) with live visualizer fields like the playhead folded
   * in at call time. Live fields are included only while a workspace is
   * mounted and `visualizer_state` is present (not dismissed). Use this for
   * send paths; keep using `context` for display (e.g. the context widget).
   */
  getContextSnapshot: () => ClientViewingContext;
  handleNewThread: () => void;
  handleToggleAuto: () => void;
  /**
   * Ask whichever surface owns this provider to point at another thread, or
   * at a new unsaved one with ``undefined``.
   *
   * The only way to change threads from inside the conversation, and a request
   * rather than a command: each surface maps it onto its own address — the
   * sidebar onto the collaboration bar's location, the thread detail page onto
   * the URL, the admin explorer onto its selection. A surface may decline, and
   * the detail page does: its address is ``/agents/threads/:threadId``, which
   * has no way to say "no thread yet".
   *
   * ``ForkAgentThreadButton`` is the caller that keeps this in the shared
   * context; it renders on all three surfaces and knows nothing about any of
   * their addresses.
   */
  navigateToThread: (threadId: string | undefined) => void;

  /** Optional admin-only per-message slot: returns a debug control to render
   *  under a message, or nothing. Undefined outside the admin thread explorer.
   *  Lives on the context (not a prop) so it reaches AgentThreadByIdView
   *  whether the thread is rendered interactively or read-only. */
  renderMessageDebugControl?: (messageSequenceNum: number) => React.ReactNode;

  /** Optional admin-only per-message slot, mirroring
   *  ``renderMessageDebugControl``: returns a knowledge-graph control to
   *  render under a *user* message, or nothing. Only called for user-role
   *  rows — the turn_context trace it opens is technically stored on the
   *  turn's first assistant message, but the affordance belongs on the
   *  request that triggered it. */
  renderMessageKnowledgeGraphControl?: (
    messageSequenceNum: number,
  ) => React.ReactNode;

  /** Optional admin-only scroll target. When the explorer navigates to a
   *  specific message (e.g. from a feedback row), AgentThreadByIdView uses this
   *  to auto-expand the concluded-task collapsible the message lives in so the
   *  scroll consumer can find it. On the context (not a prop) for the same
   *  interactive/read-only reason as ``renderMessageDebugControl``. */
  scrollToMessageSequenceNum?: number;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AIAgentThreadContext = React.createContext<
  AIAgentThreadContextValue | undefined
>(undefined);

const noop = () => {};

// Shared by the inert value's reactive `context` field and its
// `getContextSnapshot` stub so the two stay reference-equal, mirroring the
// real provider's behavior when no visualizer state is present.
const EMPTY_CLIENT_VIEWING_CONTEXT: ClientViewingContext = {
  dataset_ids: [],
  file_ids: [],
  collection_ids: [],
  device_ids: [],
  device_type_ids: [],
  session_ids: [],
  view_ids: [],
};

/**
 * A complete, inert AIAgentThreadContextValue: every state field empty, every
 * handler a no-op. Read-only host shells (today the admin thread explorer's
 * ``ReadOnlyAIAgentThreadProvider``) spread this and override only the
 * handlers they actually forward.
 *
 * Co-located with the interface on purpose: because this constant is typed as
 * the full ``AIAgentThreadContextValue``, adding a required context field
 * fails typecheck HERE, in the same file as the interface — one place to
 * update, instead of a hand-maintained structural clone drifting in the
 * admin tree.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const INERT_AI_AGENT_THREAD_CONTEXT_VALUE: AIAgentThreadContextValue = {
  // UI state
  autoEnabled: false,
  currentThreadId: undefined,
  context: EMPTY_CLIENT_VIEWING_CONTEXT,
  contextDisplayNames: {
    datasets: {},
    files: {},
    collections: {},
    deviceTypes: {},
    sessions: {},
    views: {},
  },
  modelProfile: undefined,

  // UI state setters
  setAutoEnabled: noop,
  setModelProfile: noop,

  // Convenience handlers
  handleContextDelete: noop,
  getContextSnapshot: () => EMPTY_CLIENT_VIEWING_CONTEXT,
  handleNewThread: noop,
  handleToggleAuto: noop,
  navigateToThread: noop,
};

interface AIAgentThreadProviderProps {
  children: React.ReactNode;
  /** The thread this provider is pointed at, owned by whichever address the
   *  surface uses: the URL on the detail page, the collaboration bar's
   *  location in the sidebar, local state in the admin explorer. Live —
   *  changing it switches threads without a remount, so no caller needs a
   *  ``key``. The provider holds no copy and persists nothing. */
  threadId: string | undefined;
  /** Called when something inside the conversation asks for a different
   *  thread — a fork, a new conversation from the composer. Required, because
   *  a controlled provider without it can never change threads. The surface
   *  answers by moving its address, which flows back down as a new
   *  ``threadId``, or declines and nothing happens. */
  onThreadIdChanged: (threadId: string | undefined) => void;
  /** Forwarded into context so message rows can render an admin-only debug
   *  control. Only the admin thread explorer sets this. */
  renderMessageDebugControl?: (messageSequenceNum: number) => React.ReactNode;
  /** Forwarded into context so user-message rows can render an admin-only
   *  knowledge-graph control. Only the admin thread explorer sets this. */
  renderMessageKnowledgeGraphControl?: (
    messageSequenceNum: number,
  ) => React.ReactNode;
  /** Forwarded into context so AgentThreadByIdView can auto-expand the
   *  concluded-task section a scroll target lands in. Only the admin thread
   *  explorer sets this. */
  scrollToMessageSequenceNum?: number;
}

export const AIAgentThreadProvider: React.FC<AIAgentThreadProviderProps> = ({
  children,
  threadId,
  onThreadIdChanged,
  renderMessageDebugControl,
  renderMessageKnowledgeGraphControl,
  scrollToMessageSequenceNum,
}) => {
  // UI state
  const [autoEnabled, setAutoEnabled] = React.useState(() => {
    const stored = sessionStorage.getItem("autoNavEnabled");
    return stored === "true";
  });

  // The model the user explicitly picked for the next new thread. Undefined
  // means "no pick": the start request omits the profile and the server
  // applies the org's default. Never filled in with the default itself — a
  // default sent back as a pick would pin every later new thread to whatever
  // the default was when it was read.
  const [modelProfile, setModelProfile] = React.useState<string | undefined>(
    undefined,
  );

  // A pick belongs to the unsaved thread it was made for. Any move of the
  // address — a new thread, a started one, a surface reopening on a fresh
  // chat — discards it, so the next new thread starts on the org default.
  // Adjusted during render (not in an effect) so no send can observe a stale
  // pick between the address change and the reset.
  const [pickThreadId, setPickThreadId] = React.useState(threadId);
  if (pickThreadId !== threadId) {
    setPickThreadId(threadId);
    setModelProfile(undefined);
  }

  const { context, displayNames, handleContextDelete, getContextSnapshot } =
    useClientViewingContext();

  // No thread state of its own: ``threadId`` is the thread. Keeping a copy
  // would mean two answers to "which thread is this" that have to be kept
  // agreeing — and a mount-time echo the surface would have to guard against.
  const navigateToThread = (nextThreadId: string | undefined) => {
    onThreadIdChanged(nextThreadId);
  };

  // UI State Handlers
  const handleNewThread = () => {
    navigateToThread(undefined);
    setModelProfile(undefined);
  };

  const handleToggleAuto = () => {
    setAutoEnabled((prev) => {
      const newValue = !prev;
      sessionStorage.setItem("autoNavEnabled", String(newValue));
      return newValue;
    });
  };

  const value: AIAgentThreadContextValue = {
    // UI state
    autoEnabled,
    currentThreadId: threadId,
    context,
    contextDisplayNames: displayNames,
    modelProfile,

    // UI state setters
    setAutoEnabled,
    setModelProfile,

    // Convenience handlers
    handleContextDelete,
    getContextSnapshot,
    handleNewThread,
    handleToggleAuto,
    navigateToThread,

    renderMessageDebugControl,
    renderMessageKnowledgeGraphControl,
    scrollToMessageSequenceNum,
  };

  return (
    <AIAgentThreadContext.Provider value={value}>
      {children}
    </AIAgentThreadContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAIAgentThread = (): AIAgentThreadContextValue => {
  const context = React.useContext(AIAgentThreadContext);
  if (context === undefined) {
    throw new Error(
      "useAIAgentThread must be used within an AIAgentThreadProvider",
    );
  }
  return context;
};
